/**
 * Jax Engravings — native reviews widget
 *
 * Progressive enhancement for any element with the class `.reviews-widget`
 * (sections/product-reviews.liquid and sections/all-reviews.liquid).
 * Every widget on the page is initialised independently.
 *
 * Root data attributes:
 *   data-json-url   URL that returns {"count": N, "reviews": [...]} (see templates/*.reviews-json.liquid)
 *   data-json-urls  optional comma-separated list of chunk URLs (newest first) that together hold
 *                   every review; fetched in order so no single request renders thousands of reviews
 *   data-total      total number of reviews available for this widget
 *   data-per-page   number of reviews to reveal per "Show more" click
 *
 * Hooks inside the root (all optional — missing hooks are skipped):
 *   [data-review-list]            container of .review-card articles
 *   [data-show-more]              "Show more reviews" button
 *   [data-review-results]         "Showing X of Y reviews" text
 *   [data-review-status]          visually-hidden live region
 *   [data-review-empty]           "no reviews match" message
 *   [data-review-sort]            <select> with values recent | highest | lowest | photos
 *   [data-filter-all] [data-filter-rating="N"] [data-filter-photos]   filter toggles (aria-pressed)
 *   [data-lightbox-src]           any button that opens the photo lightbox
 *   [data-review-lightbox]        <dialog> lightbox with [data-lightbox-image] + [data-lightbox-caption]
 *   [data-review-dialog]          <dialog> holding the write-a-review form
 *   [data-open-review-form]       button that opens the form dialog
 *   [data-dialog-close]           close buttons inside dialogs
 *   [data-review-posted]          success banner rendered after a successful form post
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var ROOT_SELECTOR = '.reviews-widget';
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var VERIFIED_ICON =
    '<svg aria-hidden="true" focusable="false" class="icon icon-review-check" viewBox="0 0 12 12" width="12" height="12">' +
    '<path d="M2 6.2 4.8 9 10 3.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  var SUPPORTS_DIALOG =
    typeof window.HTMLDialogElement === 'function' &&
    typeof window.HTMLDialogElement.prototype.showModal === 'function';

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function clampRating(value) {
    var n = Number(value);
    if (isNaN(n)) return 0;
    return Math.max(0, Math.min(5, n));
  }

  function starDecimal(rating) {
    var decimal = rating - Math.floor(rating);
    if (decimal >= 0.3 && decimal <= 0.7) return 0.5;
    if (decimal > 0.7) return 1;
    return 0;
  }

  function formatDate(iso) {
    if (!iso || typeof iso !== 'string') return '';
    var match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return iso;
    var month = MONTHS[toInt(match[2], 1) - 1] || '';
    return month + ' ' + toInt(match[3], 1) + ', ' + match[1];
  }

  function withWidth(url, width) {
    if (!url || typeof url !== 'string') return '';
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=' + width);
    }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function truncate(text, max) {
    var clean = String(text || '').replace(/\s+/g, ' ').trim();
    if (clean.length <= max) return clean;
    return clean.slice(0, max - 3).replace(/\s+\S*$/, '') + '...';
  }

  function pluralize(count, singular, plural) {
    return count === 1 ? singular : plural;
  }

  /** Mirrors snippets/review-count.liquid: 2568 -> "2,568". */
  function formatCount(value) {
    var n = Math.max(0, toInt(value, 0));
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        if (attrs[key] === null || attrs[key] === undefined) return;
        node.setAttribute(key, String(attrs[key]));
      });
    }
    return node;
  }

  function appendMultiline(parent, text) {
    var lines = String(text || '').split(/\r\n|\r|\n/);
    lines.forEach(function (line, i) {
      if (i > 0) parent.appendChild(document.createElement('br'));
      parent.appendChild(document.createTextNode(line));
    });
  }

  function buildStars(rating, size) {
    var value = clampRating(rating);
    var wrapper = el('div', 'review-stars review-stars--' + (size || 'medium'), {
      role: 'img',
      'aria-label': Math.round(value * 10) / 10 + ' out of 5 stars'
    });
    var star = el('span', 'rating-star', { 'aria-hidden': 'true' });
    star.setAttribute(
      'style',
      '--rating: ' + Math.floor(value) + '; --rating-max: 5; --rating-decimal: ' + starDecimal(value) + ';'
    );
    wrapper.appendChild(star);
    return wrapper;
  }

  /**
   * Builds a .review-card that matches snippets/review-card.liquid.
   * The product link is rendered whenever the payload carries `pt` + `pu`
   * (the all-reviews endpoint does; the product endpoint does not).
   * @param {Object} data review object from the JSON endpoint
   */
  function buildCard(data) {
    var rating = clampRating(data.r);
    var author = data.a || 'Anonymous';
    var body = data.b || '';
    var hasPhoto = !!data.p;

    var card = el('article', 'review-card', {
      'data-review-id': data.id,
      'data-rating': Math.round(rating),
      'data-has-photo': hasPhoto ? 'true' : 'false'
    });

    var header = el('header', 'review-card__header');
    header.appendChild(buildStars(rating, 'small'));

    var meta = el('div', 'review-card__meta');
    var authorEl = el('span', 'review-card__author');
    authorEl.textContent = author;
    meta.appendChild(authorEl);

    if (data.v) {
      var verified = el('span', 'review-card__verified');
      verified.innerHTML = VERIFIED_ICON;
      verified.appendChild(document.createTextNode(' Verified buyer'));
      meta.appendChild(verified);
    }

    /* data.s (the source) is deliberately not rendered - see snippets/review-card.liquid.
       Both renderers have to agree or reviews change wording as you page through them. */

    if (data.d) {
      var time = el('time', 'review-card__date', { datetime: data.d });
      time.textContent = formatDate(data.d);
      meta.appendChild(time);
    }

    header.appendChild(meta);
    card.appendChild(header);

    if (data.pu && data.pt) {
      var productLink = el('a', 'review-card__product', { href: data.pu });
      if (data.pi) {
        productLink.appendChild(
          el('img', null, { src: data.pi, width: 48, height: 48, alt: data.pt, loading: 'lazy' })
        );
      }
      var productTitle = el('span');
      productTitle.textContent = data.pt;
      productLink.appendChild(productTitle);
      card.appendChild(productLink);
    }

    var bodyWrap = el('div', 'review-card__body');
    var textId = 'ReviewText-' + String(data.id !== undefined && data.id !== null ? data.id : '');
    var text = el('p', 'review-card__text is-clamped', { id: textId });
    appendMultiline(text, body);
    bodyWrap.appendChild(text);
    var more = el('button', 'review-card__more link', {
      type: 'button',
      'aria-expanded': 'false',
      'aria-controls': textId,
      hidden: ''
    });
    more.textContent = 'Read more';
    bodyWrap.appendChild(more);
    card.appendChild(bodyWrap);

    if (hasPhoto) {
      var photoButton = el('button', 'review-card__photo', {
        type: 'button',
        'aria-label': 'View photo from ' + author,
        'data-lightbox-src': withWidth(data.p, 800),
        'data-lightbox-caption': author + ' — ' + truncate(body, 140)
      });
      photoButton.appendChild(
        el('img', null, {
          src: withWidth(data.p, 200),
          srcset: withWidth(data.p, 200) + ' 200w, ' + withWidth(data.p, 400) + ' 400w',
          sizes: '96px',
          width: data.pw || 200,
          height: data.ph || 200,
          alt: 'Customer photo from ' + author,
          loading: 'lazy'
        })
      );
      card.appendChild(photoButton);
    }

    if (data.y) {
      var reply = el('div', 'review-card__reply');
      var label = el('span', 'review-card__reply-label');
      label.textContent = 'Reply from Jax Engravings';
      reply.appendChild(label);
      var replyText = el('p');
      appendMultiline(replyText, data.y);
      reply.appendChild(replyText);
      card.appendChild(reply);
    }

    return card;
  }

  /* ------------------------------------------------------------------ */
  /* Dialog helpers (native <dialog> with a graceful fallback)           */
  /* ------------------------------------------------------------------ */

  var openDialogs = [];

  function lockScroll() {
    document.documentElement.classList.add('reviews-dialog-open');
  }

  function unlockScroll() {
    if (openDialogs.length === 0) document.documentElement.classList.remove('reviews-dialog-open');
  }

  function openDialog(dialog, trigger) {
    if (!dialog) return;
    if (dialog.__reviewsOpen) return;
    dialog.__reviewsOpen = true;
    dialog.__reviewsTrigger = trigger || document.activeElement;
    openDialogs.push(dialog);
    lockScroll();

    if (SUPPORTS_DIALOG && typeof dialog.showModal === 'function') {
      if (dialog.hasAttribute('open')) dialog.removeAttribute('open');
      try {
        dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
        dialog.classList.add('is-open-fallback');
      }
    } else {
      dialog.setAttribute('open', '');
      dialog.classList.add('is-open-fallback');
    }

    var focusTarget =
      dialog.querySelector('[autofocus]') ||
      dialog.querySelector('input:not([type="hidden"]), select, textarea') ||
      dialog.querySelector('[data-dialog-close]') ||
      dialog.querySelector('button, [href]');
    if (focusTarget && typeof focusTarget.focus === 'function') {
      window.requestAnimationFrame(function () {
        focusTarget.focus();
      });
    }
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.__reviewsOpen) return;
    if (SUPPORTS_DIALOG && typeof dialog.close === 'function' && !dialog.classList.contains('is-open-fallback')) {
      try {
        dialog.close();
        return; // `close` event handles cleanup
      } catch (error) {
        /* fall through to manual cleanup */
      }
    }
    dialog.removeAttribute('open');
    dialog.classList.remove('is-open-fallback');
    finishClose(dialog);
  }

  function finishClose(dialog) {
    dialog.__reviewsOpen = false;
    var index = openDialogs.indexOf(dialog);
    if (index > -1) openDialogs.splice(index, 1);
    unlockScroll();
    var trigger = dialog.__reviewsTrigger;
    dialog.__reviewsTrigger = null;
    if (trigger && typeof trigger.focus === 'function' && document.body.contains(trigger)) {
      trigger.focus();
    }
  }

  function wireDialog(dialog) {
    if (!dialog || dialog.__reviewsWired) return;
    dialog.__reviewsWired = true;

    dialog.addEventListener('close', function () {
      if (dialog.__reviewsOpen) finishClose(dialog);
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDialog(dialog);
    });

    dialog.addEventListener('click', function (event) {
      var closeButton = event.target.closest ? event.target.closest('[data-dialog-close]') : null;
      if (closeButton) {
        event.preventDefault();
        closeDialog(dialog);
        return;
      }
      // Backdrop click: the dialog element itself is the only thing under the pointer.
      if (event.target === dialog) closeDialog(dialog);
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var dialog = openDialogs[openDialogs.length - 1];
    if (dialog && dialog.classList.contains('is-open-fallback')) {
      event.preventDefault();
      closeDialog(dialog);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Widget                                                              */
  /* ------------------------------------------------------------------ */

  function ReviewsWidget(root) {
    this.root = root;
    this.jsonUrl = root.getAttribute('data-json-url') || '';
    this.jsonUrls = (root.getAttribute('data-json-urls') || this.jsonUrl)
      .split(',')
      .map(function (url) { return url.trim(); })
      .filter(Boolean);
    this.loadedChunks = 0;
    this.total = Math.max(0, toInt(root.getAttribute('data-total'), 0));
    this.perPage = Math.max(1, toInt(root.getAttribute('data-per-page'), 10));

    this.list = root.querySelector('[data-review-list]');
    this.showMoreButton = root.querySelector('[data-show-more]');
    this.results = root.querySelector('[data-review-results]');
    this.status = root.querySelector('[data-review-status]');
    this.emptyMessage = root.querySelector('[data-review-empty]');
    this.sortSelect = root.querySelector('[data-review-sort]');
    this.lightbox = root.querySelector('[data-review-lightbox]');
    this.formDialog = root.querySelector('[data-review-dialog]');

    this.sort = 'recommended';
    this.filter = { type: 'all', value: 0 };
    this.shown = this.perPage;
    this.fetched = null; // array of items once the JSON endpoint has been loaded
    this.fetchPromise = null;
    this.elementsById = {};
    this.errorMessage = null;

    this.localItems = this.readLocalItems();
    this.lastVisibleCount = this.localItems.length;

    this.bindEvents();
    this.prepareClamps(this.localItems.map(function (item) { return item.el; }));
    this.handleFormState();
  }

  ReviewsWidget.prototype.readLocalItems = function () {
    var self = this;
    if (!this.list) return [];
    var cards = Array.prototype.slice.call(this.list.querySelectorAll('.review-card'));
    return cards.map(function (card, index) {
      var id = card.getAttribute('data-review-id') || 'local-' + index;
      self.elementsById[id] = card;
      return {
        id: id,
        r: clampRating(card.getAttribute('data-rating')),
        hasPhoto: card.getAttribute('data-has-photo') === 'true',
        index: index,
        data: null,
        el: card
      };
    });
  };

  ReviewsWidget.prototype.bindEvents = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var lightboxTrigger = target.closest('[data-lightbox-src]');
      if (lightboxTrigger && self.root.contains(lightboxTrigger)) {
        event.preventDefault();
        self.openLightbox(lightboxTrigger);
        return;
      }

      var more = target.closest('.review-card__more');
      if (more) {
        event.preventDefault();
        self.toggleReadMore(more);
        return;
      }

      var openForm = target.closest('[data-open-review-form]');
      if (openForm) {
        event.preventDefault();
        if (self.formDialog) openDialog(self.formDialog, openForm);
        return;
      }

      var ratingFilter = target.closest('[data-filter-rating]');
      if (ratingFilter) {
        event.preventDefault();
        var value = toInt(ratingFilter.getAttribute('data-filter-rating'), 0);
        if (self.filter.type === 'rating' && self.filter.value === value) {
          self.setFilter('all', 0);
        } else {
          self.setFilter('rating', value);
        }
        return;
      }

      var photosFilter = target.closest('[data-filter-photos]');
      if (photosFilter) {
        event.preventDefault();
        self.setFilter(self.filter.type === 'photos' ? 'all' : 'photos', 0);
        return;
      }

      var allFilter = target.closest('[data-filter-all]');
      if (allFilter) {
        event.preventDefault();
        self.setFilter('all', 0);
      }
    });

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', function () {
        self.setSort(self.sortSelect.value);
      });
    }

    if (this.showMoreButton) {
      this.showMoreButton.addEventListener('click', function (event) {
        event.preventDefault();
        self.showMore();
      });
    }

    wireDialog(this.lightbox);
    wireDialog(this.formDialog);

    var resizeTimer = null;
    this.onResize = function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        self.recheckClamps();
      }, 150);
    };
    window.addEventListener('resize', this.onResize);
  };

  /** Called on shopify:section:unload so theme-editor re-renders do not leak window listeners. */
  ReviewsWidget.prototype.destroy = function () {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.onResize = null;
  };

  /* Form state -------------------------------------------------------- */

  ReviewsWidget.prototype.handleFormState = function () {
    var self = this;
    var posted = this.root.querySelector('[data-review-posted]');
    if (posted) {
      window.requestAnimationFrame(function () {
        if (typeof posted.scrollIntoView === 'function') {
          posted.scrollIntoView({ block: 'center' });
        }
        posted.focus();
      });
      return;
    }

    // Server-rendered validation errors: the dialog is rendered with `open`; re-open it as a modal.
    if (this.formDialog && this.formDialog.hasAttribute('open')) {
      this.formDialog.removeAttribute('open');
      window.requestAnimationFrame(function () {
        openDialog(self.formDialog, self.root.querySelector('[data-open-review-form]'));
      });
    }
  };

  /* Sorting / filtering ----------------------------------------------- */

  ReviewsWidget.prototype.hasMoreChunks = function () {
    return typeof window.fetch === 'function' && this.loadedChunks < this.jsonUrls.length;
  };

  ReviewsWidget.prototype.loadedCount = function () {
    return this.fetched ? this.fetched.length : this.localItems.length;
  };

  /**
   * Whether more data must be fetched before rendering.
   * Sorting/filtering needs every chunk; "show more" only needs enough to fill the next page.
   */
  ReviewsWidget.prototype.needsRemoteData = function (forShowMore) {
    if (!this.hasMoreChunks()) return false;
    if (!this.fetched && this.total <= this.localItems.length) return false;
    if (forShowMore) return this.loadedCount() < this.shown + this.perPage;
    return true;
  };

  ReviewsWidget.prototype.setFilter = function (type, value) {
    this.filter = { type: type, value: value };
    this.shown = this.perPage;
    this.syncFilterControls();
    this.refreshWithData(false);
  };

  ReviewsWidget.prototype.setSort = function (sort) {
    var allowed = ['recommended', 'recent', 'highest', 'lowest', 'photos'];
    this.sort = allowed.indexOf(sort) > -1 ? sort : 'recommended';
    this.shown = this.perPage;
    this.refreshWithData(false);
  };

  ReviewsWidget.prototype.syncFilterControls = function () {
    var filter = this.filter;
    var controls = this.root.querySelectorAll('[data-filter-rating], [data-filter-photos], [data-filter-all]');
    Array.prototype.forEach.call(controls, function (control) {
      var pressed = false;
      if (control.hasAttribute('data-filter-rating')) {
        pressed = filter.type === 'rating' && toInt(control.getAttribute('data-filter-rating'), 0) === filter.value;
      } else if (control.hasAttribute('data-filter-photos')) {
        pressed = filter.type === 'photos';
      } else if (control.hasAttribute('data-filter-all')) {
        pressed = filter.type === 'all';
      }
      control.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  };

  ReviewsWidget.prototype.refreshWithData = function (focusNew) {
    var self = this;
    if (!this.needsRemoteData()) {
      this.render(focusNew);
      return;
    }
    this.setLoading(true);
    this.ensureData(true).then(function (ok) {
      self.setLoading(false);
      if (!ok) self.showError('Some reviews could not be loaded. Showing the reviews already on this page.');
      self.render(focusNew);
    });
  };

  ReviewsWidget.prototype.getItems = function () {
    return this.fetched || this.localItems;
  };

  ReviewsWidget.prototype.getFilteredSorted = function () {
    var filter = this.filter;
    var items = this.getItems().filter(function (item) {
      if (filter.type === 'rating') return Math.round(item.r) === filter.value;
      if (filter.type === 'photos') return item.hasPhoto;
      return true;
    });

    var sort = this.sort;
    items = items.slice().sort(function (a, b) {
      /*
        Recommended: best rating first, newest first inside each rating - so the top of the
        list is the most recent five stars, then the most recent fours, and so on.

        The tiebreak below carries the second half of that. `index` is the order the reviews
        arrive in, which is newest first, so falling through to it gives the date ordering
        without a second comparison.

        Worth knowing: this is the same comparator as 'highest'. That option already sorted by
        rating and broke ties by date, which is exactly what was asked for here - the new part
        is the name and being the default, not the ordering.
      */
      if (sort === 'recommended' && a.r !== b.r) return b.r - a.r;
      if (sort === 'highest' && a.r !== b.r) return b.r - a.r;
      if (sort === 'lowest' && a.r !== b.r) return a.r - b.r;
      if (sort === 'photos' && a.hasPhoto !== b.hasPhoto) return a.hasPhoto ? -1 : 1;
      return a.index - b.index;
    });

    return items;
  };

  /* Data loading ------------------------------------------------------ */

  /**
   * Loads the next JSON chunk (or every remaining chunk when `all` is true) and appends the
   * reviews to this.fetched. Resolves true when at least the requested data is available.
   * @param {boolean} all
   */
  ReviewsWidget.prototype.ensureData = function (all) {
    var self = this;
    if (!this.hasMoreChunks()) return Promise.resolve(!!this.fetched);
    if (this.fetchPromise) {
      return this.fetchPromise.then(function (ok) {
        return ok && all && self.hasMoreChunks() ? self.ensureData(true) : ok;
      });
    }

    var urls = all ? this.jsonUrls.slice(this.loadedChunks) : [this.jsonUrls[this.loadedChunks]];
    var requests = urls.map(function (url) {
      // No `Accept: application/json` here: Shopify answers that with its own product JSON
      // instead of rendering the `?view=reviews-json` alternate template.
      return window
        .fetch(url, { headers: { Accept: 'text/html, */*' }, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Reviews request failed: ' + response.status);
          return response.text();
        })
        .then(function (text) {
          return JSON.parse(text);
        })
        .then(function (payload) {
          var reviews = payload && Array.isArray(payload.reviews) ? payload.reviews : null;
          if (!reviews) throw new Error('Reviews payload malformed');
          return reviews;
        });
    });

    this.fetchPromise = Promise.all(requests)
      .then(function (chunks) {
        var items = self.fetched ? self.fetched.slice() : [];
        chunks.forEach(function (reviews) {
          reviews.forEach(function (data) {
            var index = items.length;
            var id = data && data.id !== undefined && data.id !== null ? String(data.id) : 'remote-' + index;
            items.push({
              id: id,
              r: clampRating(data ? data.r : 0),
              hasPhoto: !!(data && data.p),
              index: index,
              data: data || {},
              el: self.elementsById[id] || null
            });
          });
        });
        if (!self.fetched && self.localItems.length) {
          // Keep server-rendered reviews the JSON cache does not know about (reviews added in the
          // admin after the cache was generated). They are the newest, so they go first.
          var known = {};
          items.forEach(function (item) { known[item.id] = true; });
          var extra = self.localItems.filter(function (item) { return !known[item.id]; });
          if (extra.length) {
            items = extra.concat(items);
            items.forEach(function (item, i) { item.index = i; });
          }
        }
        self.loadedChunks += chunks.length;
        self.fetchPromise = null;
        if (items.length === 0 && self.localItems.length > 0) {
          // Endpoint returned nothing usable; keep what the server rendered.
          return false;
        }
        self.fetched = items;
        self.total = Math.max(self.total, items.length);
        return true;
      })
      .catch(function (error) {
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[reviews] ' + (error && error.message ? error.message : error));
        }
        self.fetchPromise = null;
        return false;
      });

    return this.fetchPromise;
  };

  /* Rendering ---------------------------------------------------------- */

  ReviewsWidget.prototype.render = function (focusNew) {
    if (!this.list) return;
    var self = this;
    var items = this.getFilteredSorted();
    var visible = items.slice(0, this.shown);
    var previousCount = focusNew ? this.lastVisibleCount : -1;

    var fragment = document.createDocumentFragment();
    var newCards = [];
    visible.forEach(function (item) {
      if (!item.el) {
        item.el = buildCard(item.data || {});
        self.elementsById[item.id] = item.el;
        newCards.push(item.el);
      } else if (!item.el.parentNode) {
        newCards.push(item.el);
      }
      fragment.appendChild(item.el);
    });

    while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
    this.list.appendChild(fragment);
    this.lastVisibleCount = visible.length;

    // Total for the results text: exact once data is loaded, otherwise the server-provided total.
    var filteredTotal = items.length;
    if (this.filter.type === 'all' && (!this.fetched || this.hasMoreChunks())) {
      filteredTotal = Math.max(this.total, items.length);
    }

    this.updateResults(visible.length, filteredTotal);

    if (this.emptyMessage) this.emptyMessage.hidden = visible.length > 0;

    if (this.showMoreButton) {
      var canLoadMore = visible.length < filteredTotal;
      this.showMoreButton.hidden = !canLoadMore;
      if (canLoadMore) this.showMoreButton.textContent = 'Show more reviews';
    }

    this.prepareClamps(newCards);

    if (focusNew && previousCount >= 0 && visible.length > previousCount) {
      var firstNew = visible[previousCount].el;
      if (firstNew) {
        firstNew.setAttribute('tabindex', '-1');
        window.requestAnimationFrame(function () {
          firstNew.focus({ preventScroll: false });
        });
      }
      this.announce(
        'Showing ' + visible.length + ' of ' + formatCount(filteredTotal) + ' ' + pluralize(filteredTotal, 'review', 'reviews') + '.'
      );
    } else if (visible.length === 0) {
      this.announce('No reviews match this filter.');
    }
  };

  ReviewsWidget.prototype.updateResults = function (shown, total) {
    if (!this.results) return;
    this.results.textContent =
      'Showing ' + shown + ' of ' + formatCount(total) + ' ' + pluralize(total, 'review', 'reviews');
  };

  ReviewsWidget.prototype.showMore = function () {
    var self = this;
    if (!this.showMoreButton || this.showMoreButton.getAttribute('aria-disabled') === 'true') return;

    var proceed = function () {
      self.shown += self.perPage;
      self.render(true);
    };

    if (!this.needsRemoteData(true)) {
      proceed();
      return;
    }

    this.setLoading(true);
    this.ensureData(false).then(function (ok) {
      self.setLoading(false);
      if (!ok) {
        self.showError('Could not load more reviews right now. Please try again.');
        self.showMoreButton.textContent = 'Try again';
        self.showMoreButton.focus();
        return;
      }
      proceed();
    });
  };

  ReviewsWidget.prototype.setLoading = function (loading) {
    if (this.list) {
      if (loading) this.list.setAttribute('aria-busy', 'true');
      else this.list.removeAttribute('aria-busy');
    }
    if (this.showMoreButton) {
      // aria-disabled (not disabled) so keyboard focus stays on the button while loading
      if (loading) this.showMoreButton.setAttribute('aria-disabled', 'true');
      else this.showMoreButton.removeAttribute('aria-disabled');
      if (loading) this.showMoreButton.textContent = 'Loading…';
    }
    if (loading) {
      this.clearError();
      this.announce('Loading reviews.');
    }
  };

  ReviewsWidget.prototype.announce = function (message) {
    if (!this.status) return;
    var status = this.status;
    status.textContent = '';
    window.setTimeout(function () {
      status.textContent = message;
    }, 50);
  };

  ReviewsWidget.prototype.showError = function (message) {
    this.clearError();
    var anchor = this.showMoreButton ? this.showMoreButton.parentNode : this.list;
    if (!anchor) return;
    var error = el('p', 'review-list__error caption-large', { role: 'alert' });
    error.textContent = message;
    this.errorMessage = error;
    if (this.showMoreButton) anchor.insertBefore(error, this.showMoreButton);
    else anchor.appendChild(error);
  };

  ReviewsWidget.prototype.clearError = function () {
    if (this.errorMessage && this.errorMessage.parentNode) {
      this.errorMessage.parentNode.removeChild(this.errorMessage);
    }
    this.errorMessage = null;
  };

  /* Read more ---------------------------------------------------------- */

  /**
   * Decides which cards need a "Read more" button. Cards are rendered with `is-clamped`
   * already applied (server and client) so nothing shifts on load; here we batch the
   * writes and reads so the browser performs a single layout pass.
   */
  ReviewsWidget.prototype.prepareClamps = function (cards) {
    if (!cards || !cards.length) return;
    window.requestAnimationFrame(function () {
      var entries = [];
      cards.forEach(function (card) {
        if (!card) return;
        var text = card.querySelector('.review-card__text');
        var button = card.querySelector('.review-card__more');
        if (!text || !button) return;
        if (button.getAttribute('aria-expanded') === 'true') return;
        entries.push({ text: text, button: button });
      });
      // 1. writes
      entries.forEach(function (entry) {
        entry.text.classList.add('is-clamped');
      });
      // 2. reads
      entries.forEach(function (entry) {
        entry.overflows = entry.text.scrollHeight > entry.text.clientHeight + 1;
      });
      // 3. writes
      entries.forEach(function (entry) {
        if (entry.overflows) {
          entry.button.hidden = false;
        } else {
          entry.text.classList.remove('is-clamped');
          entry.button.hidden = true;
        }
      });
    });
  };

  ReviewsWidget.prototype.recheckClamps = function () {
    if (!this.list) return;
    this.prepareClamps(Array.prototype.slice.call(this.list.querySelectorAll('.review-card')));
  };

  ReviewsWidget.prototype.toggleReadMore = function (button) {
    var body = button.closest('.review-card__body');
    var text = body ? body.querySelector('.review-card__text') : null;
    if (!text) return;
    var expanded = button.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      text.classList.add('is-clamped');
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'Read more';
    } else {
      text.classList.remove('is-clamped');
      button.setAttribute('aria-expanded', 'true');
      button.textContent = 'Show less';
    }
  };

  /* Lightbox ------------------------------------------------------------ */

  ReviewsWidget.prototype.openLightbox = function (trigger) {
    var src = trigger.getAttribute('data-lightbox-src');
    if (!src) return;
    var caption = trigger.getAttribute('data-lightbox-caption') || '';

    if (!this.lightbox) {
      // No lightbox markup available: open the full-size image directly.
      window.open(src, '_blank', 'noopener');
      return;
    }

    var image = this.lightbox.querySelector('[data-lightbox-image]');
    var captionEl = this.lightbox.querySelector('[data-lightbox-caption]');
    if (image) {
      var thumb = trigger.querySelector('img');
      var width = thumb ? toInt(thumb.getAttribute('width'), 0) : 0;
      var height = thumb ? toInt(thumb.getAttribute('height'), 0) : 0;
      if (width > 0 && height > 0) {
        image.setAttribute('width', width);
        image.setAttribute('height', height);
      }
      // The caption is shown in the figcaption; the alt describes the image only.
      image.setAttribute('alt', thumb && thumb.getAttribute('alt') ? thumb.getAttribute('alt') : 'Customer photo');
      image.setAttribute('src', src);
    }
    if (captionEl) captionEl.textContent = caption;

    openDialog(this.lightbox, trigger);
  };

  /* ------------------------------------------------------------------ */
  /* Bootstrap                                                           */
  /* ------------------------------------------------------------------ */

  function initWithin(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var roots = Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));
    if (container !== document && container.matches && container.matches(ROOT_SELECTOR)) roots.unshift(container);
    roots.forEach(function (root) {
      if (root.__reviewsWidget) return;
      try {
        root.__reviewsWidget = new ReviewsWidget(root);
      } catch (error) {
        if (window.console && typeof window.console.error === 'function') {
          window.console.error('[reviews] failed to initialise widget', error);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initWithin(document);
    });
  } else {
    initWithin(document);
  }

  // Theme editor: sections are re-rendered when settings change.
  document.addEventListener('shopify:section:load', function (event) {
    initWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target;
    if (!scope || !scope.querySelectorAll) return;
    var roots = Array.prototype.slice.call(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    roots.forEach(function (root) {
      if (root.__reviewsWidget && typeof root.__reviewsWidget.destroy === 'function') {
        root.__reviewsWidget.destroy();
      }
      root.__reviewsWidget = null;
    });
  });

  /* ------------------------------------------------------------------ */
  /* Homepage carousel: plain horizontal scroller with prev/next buttons  */
  /* ------------------------------------------------------------------ */

  function initCarousel(scroller) {
    if (!scroller || scroller.__reviewsCarousel) return;
    scroller.__reviewsCarousel = true;
    var track = scroller.querySelector('.reviews-carousel__track');
    var prev = scroller.querySelector('.slider-button--prev');
    var next = scroller.querySelector('.slider-button--next');
    var current = scroller.querySelector('.slider-counter--current');
    var total = scroller.querySelector('.slider-counter--total');
    if (!track) return;
    var items = Array.prototype.slice.call(track.querySelectorAll('.reviews-carousel__item'));
    if (total) total.textContent = String(items.length);

    function step() {
      var first = items[0];
      if (!first) return track.clientWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function perView() {
      return Math.max(1, Math.round(track.clientWidth / step()));
    }

    function update() {
      var index = Math.round(track.scrollLeft / step());
      if (current) current.textContent = String(Math.min(items.length, index + 1));
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    function scrollBy(dir) {
      track.scrollBy({ left: dir * step() * perView(), behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
    if (next) next.addEventListener('click', function () { scrollBy(1); });
    track.addEventListener('scroll', function () {
      window.clearTimeout(scroller.__reviewsScrollTimer);
      scroller.__reviewsScrollTimer = window.setTimeout(update, 80);
    });
    window.addEventListener('resize', function () {
      window.clearTimeout(scroller.__reviewsResizeTimer);
      scroller.__reviewsResizeTimer = window.setTimeout(update, 150);
    });

    /* ---- drag to scroll -------------------------------------------- */
    /*
      Mouse only, deliberately. Touch already scrolls this natively, with momentum and rubber
      banding the browser does better than any script - taking those events over would replace
      something good with something worse.
    */
    var dragging = false;
    var didDrag = false;
    var startX = 0;
    var startLeft = 0;

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      didDrag = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });

    track.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var dx = event.clientX - startX;
      // A few pixels of travel while clicking a link is a click, not a drag.
      if (!didDrag && Math.abs(dx) < 4) return;
      if (!didDrag) {
        didDrag = true;
        track.classList.add('is-dragging');
        // setPointerCapture throws if the pointer is no longer active - a release that landed
        // outside the window, a cancelled gesture. Uncaught it would abort this handler with
        // dragging still true, leaving the drag stuck on. Capture is a nicety; drag works without.
        try {
          if (track.setPointerCapture) track.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
      track.scrollLeft = startLeft - dx;
      event.preventDefault();
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      update();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    /*
      Every card carries a link to its product. Without this, letting go of a drag on top of one
      navigates away - the drag ends in a click as far as the browser is concerned. Capture phase,
      so it is stopped before the link ever sees it.
    */
    track.addEventListener(
      'click',
      function (event) {
        if (!didDrag) return;
        event.preventDefault();
        event.stopPropagation();
        didDrag = false;
      },
      true
    );

    /* ---- gentle autoplay ------------------------------------------- */
    /*
      One card at a time on a timer, rather than the continuous drift the promo strip uses. These
      cards hold sentences somebody has to read, and text that never stops moving cannot be read.
      This holds each card still long enough to finish it.
    */
    var AUTOPLAY_MS = 5000;
    var paused = false;
    var visible = true;
    var autoplayTimer = null;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function atEnd() {
      return track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }

    function advance() {
      if (paused || dragging || !visible || document.hidden) return;
      if (atEnd()) track.scrollTo({ left: 0, behavior: 'smooth' });
      else track.scrollBy({ left: step(), behavior: 'smooth' });
    }

    if (!reduced) {
      autoplayTimer = window.setInterval(advance, AUTOPLAY_MS);

      // Anyone reading, hovering or tabbing through has taken over; stop moving under them.
      ['pointerenter', 'focusin'].forEach(function (name) {
        scroller.addEventListener(name, function () { paused = true; });
      });
      ['pointerleave', 'focusout'].forEach(function (name) {
        scroller.addEventListener(name, function () { paused = false; });
      });

      // No point animating a carousel nobody is looking at, and scrolling it while off screen
      // means it is somewhere unexpected when it comes back into view.
      if (window.IntersectionObserver) {
        new window.IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
        }, { threshold: 0.2 }).observe(scroller);
      }

      document.addEventListener('shopify:section:unload', function (event) {
        if (event.target && event.target.contains(scroller) && autoplayTimer) {
          window.clearInterval(autoplayTimer);
        }
      });
    }

    update();
  }

  /* ------------------------------------------------------------------ */
  /* Customer photos: a strip that drifts                                 */
  /* ------------------------------------------------------------------ */

  /**
   * Photographs, unlike the review cards, carry nothing that has to be read, so they can move
   * continuously rather than a card at a time.
   *
   * Driven by scrollLeft rather than a CSS transform, because this strip is also something people
   * scroll, drag and click into a lightbox - a transform would fight all three. The cost is that
   * looping has to be handled here: the thumbnails are cloned once, so passing the halfway point can
   * be rewound by exactly half the width to a frame identical to the one before it.
   */
  function initPhotoStrip(strip) {
    if (!strip || strip.__photoMarquee) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Below 750px this is a swipeable strip and should stay one; drifting under a thumb being
    // scrolled is worse than not moving at all.
    var wide = window.matchMedia && window.matchMedia('(min-width: 750px)').matches;
    if (reduced || !wide) return;
    if (strip.scrollWidth <= strip.clientWidth + 4) return; // nothing to scroll

    strip.__photoMarquee = true;

    var originals = Array.prototype.slice.call(strip.children);
    originals.forEach(function (node) {
      var clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      strip.appendChild(clone);
    });

    strip.classList.add('is-marquee');

    var SPEED = 22; // px per second - slow enough to look at
    var paused = false;
    var visible = true;
    var dragging = false;
    var didDrag = false;
    var startX = 0;
    var startLeft = 0;
    var last = null;
    var pos = strip.scrollLeft;

    function half() {
      return strip.scrollWidth / 2;
    }

    function frame(now) {
      if (last === null) last = now;
      var dt = now - last;
      last = now;

      if (!paused && !dragging && visible && !document.hidden) {
        // Accumulated as a float: at 22px/s a frame moves under half a pixel, and assigning that
        // straight to scrollLeft rounds it away to nothing every time.
        pos += (SPEED * dt) / 1000;
        if (pos >= half()) pos -= half();
        strip.scrollLeft = pos;
      } else {
        pos = strip.scrollLeft;
      }

      window.requestAnimationFrame(frame);
    }

    window.requestAnimationFrame(frame);

    ['pointerenter', 'focusin'].forEach(function (name) {
      strip.addEventListener(name, function () { paused = true; });
    });
    ['pointerleave', 'focusout'].forEach(function (name) {
      strip.addEventListener(name, function () { paused = false; });
    });

    if (window.IntersectionObserver) {
      new window.IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0.1 }).observe(strip);
    }

    /* drag, mouse only - touch scrolls this natively */
    strip.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      didDrag = false;
      startX = event.clientX;
      startLeft = strip.scrollLeft;
    });

    strip.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var dx = event.clientX - startX;
      if (!didDrag && Math.abs(dx) < 4) return;
      if (!didDrag) {
        didDrag = true;
        strip.classList.add('is-dragging');
        try {
          if (strip.setPointerCapture) strip.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
      strip.scrollLeft = startLeft - dx;
      event.preventDefault();
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      strip.classList.remove('is-dragging');
      // Hand the loop back a position inside the first half, or it rewinds from the wrong place.
      pos = strip.scrollLeft;
      if (pos >= half()) pos -= half();
      strip.scrollLeft = pos;
    }

    strip.addEventListener('pointerup', endDrag);
    strip.addEventListener('pointercancel', endDrag);

    // Each thumbnail opens a lightbox. Without this, letting go of a drag on one opens it.
    strip.addEventListener(
      'click',
      function (event) {
        if (!didDrag) return;
        event.preventDefault();
        event.stopPropagation();
        didDrag = false;
      },
      true
    );
  }

  function initPhotoStripsWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    Array.prototype.forEach.call(scope.querySelectorAll('.review-photos__strip'), initPhotoStrip);
  }

  function initCarouselsWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var list = Array.prototype.slice.call(scope.querySelectorAll('[data-reviews-scroller]'));
    if (scope.matches && scope.matches('[data-reviews-scroller]')) list.unshift(scope);
    list.forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCarouselsWithin(document);
      initPhotoStripsWithin(document);
    });
  } else {
    initCarouselsWithin(document);
    initPhotoStripsWithin(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initCarouselsWithin(event.target);
  });

})();
